import { FormEvent, useEffect, useRef, useState } from 'react';
import { IconClose, IconEmoji } from './icons';
import { ChatComment, getChatName, setChatName } from './utils';

interface FeedProps {
  comments: ChatComment[];
}

interface ComposeProps {
  onSend: (message: string, displayName: string) => Promise<void>;
  onFocusChange?: (focused: boolean) => void;
  /** Soft keyboard visible — show Send + emoji. */
  keyboardOpen?: boolean;
}

const NAME_COLORS = [
  '#20D5EC',
  '#FFB300',
  '#7B61FF',
  '#57C785',
  '#FF6A3D',
  '#FE2C55',
  '#4EA1FF',
];

const EMOJIS = ['❤️', '🔥', '😂', '👏', '😍', '🎉', '👍', '✨', '😮', '💯'];

function nameColor(name: string): string {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) >>> 0;
  return NAME_COLORS[h % NAME_COLORS.length];
}

function FbLogo() {
  return (
    <svg className="il-fb-logo" viewBox="0 0 24 24" width="11" height="11" aria-label="Facebook">
      <circle cx="12" cy="12" r="12" fill="#1877F2" />
      <path
        fill="#fff"
        d="M13.5 8.5h1.7V6.1c-.3 0-.9-.1-1.7-.1-1.7 0-2.9 1.1-2.9 3V11H8.5v2.6h2.1V20h2.7v-6.4h2.2l.4-2.6h-2.6V9.3c0-.7.2-1.3.8-1.3z"
      />
    </svg>
  );
}

export function ChatFeed({ comments }: FeedProps) {
  const feedRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = feedRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [comments]);

  return (
    <div className="il-chat-feed" ref={feedRef}>
      {comments.slice(-50).map((c) => {
        const isHost = c.source === 'host';
        return (
          <div
            key={c.id}
            className={`il-chat-pill${isHost ? ' is-host' : ''}`}
          >
            <span
              className="il-chat-from"
              style={isHost ? undefined : { color: nameColor(c.from) }}
            >
              {isHost ? (
                <span className="il-host-badge" aria-label="Admin">
                  ADMIN
                </span>
              ) : (
                <>
                  {c.source === 'facebook' && <FbLogo />}
                  {c.from}
                </>
              )}
            </span>
            <span className="il-chat-msg">{c.message}</span>
          </div>
        );
      })}
    </div>
  );
}

export function ChatCompose({ onSend, onFocusChange, keyboardOpen = false }: ComposeProps) {
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [showEmoji, setShowEmoji] = useState(false);
  const [askName, setAskName] = useState(false);
  const [nameDraft, setNameDraft] = useState(getChatName() || '');
  const pendingMsg = useRef<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!keyboardOpen) setShowEmoji(false);
  }, [keyboardOpen]);

  const actuallySend = async (message: string, displayName: string) => {
    try {
      setBusy(true);
      await onSend(message, displayName);
      setDraft('');
      setShowEmoji(false);
    } finally {
      setBusy(false);
    }
  };

  const send = async (e?: FormEvent) => {
    e?.preventDefault();
    if (!draft.trim() || busy) return;
    const msg = draft.trim();
    const existing = getChatName();
    if (!existing) {
      pendingMsg.current = msg;
      setAskName(true);
      return;
    }
    await actuallySend(msg, existing);
  };

  const confirmName = async (e: FormEvent) => {
    e.preventDefault();
    if (!nameDraft.trim()) return;
    setChatName(nameDraft);
    setAskName(false);
    const msg = pendingMsg.current || draft.trim();
    pendingMsg.current = null;
    if (msg) await actuallySend(msg, nameDraft.trim());
  };

  return (
    <>
      <form className="il-compose" onSubmit={send}>
        <input
          ref={inputRef}
          className="il-compose-input"
          placeholder="Say something…"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onFocus={() => onFocusChange?.(true)}
          onBlur={() => {
            // Delay so emoji/send taps register before focus clears
            window.setTimeout(() => {
              if (document.activeElement === inputRef.current) return;
              onFocusChange?.(false);
            }, 180);
          }}
          maxLength={500}
          disabled={busy}
          enterKeyHint="send"
        />
        {keyboardOpen && (
          <>
            <button
              type="button"
              className="il-compose-emoji"
              aria-label="Emoji"
              aria-expanded={showEmoji}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => setShowEmoji((v) => !v)}
            >
              <IconEmoji size={20} />
            </button>
            <button
              type="submit"
              className="il-compose-send"
              disabled={busy || !draft.trim()}
              aria-label="Send"
              onMouseDown={(e) => e.preventDefault()}
            >
              Send
            </button>
          </>
        )}
      </form>

      {keyboardOpen && showEmoji && (
        <div className="il-emoji-panel">
          {EMOJIS.map((em) => (
            <button
              key={em}
              type="button"
              className="il-emoji-btn"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => {
                setDraft((d) => `${d}${em}`);
                setShowEmoji(false);
                inputRef.current?.focus();
              }}
            >
              {em}
            </button>
          ))}
        </div>
      )}

      {askName && (
        <div className="il-name-modal" role="dialog">
          <div className="il-name-card">
            <button
              type="button"
              className="il-sheet-x"
              onClick={() => {
                setAskName(false);
                pendingMsg.current = null;
              }}
              aria-label="Close"
            >
              <IconClose size={16} />
            </button>
            <h3>Enter your nickname</h3>
            <p>This name shows in LIVE chat</p>
            <form onSubmit={confirmName}>
              <input
                className="il-name-input"
                autoFocus
                placeholder="Nickname"
                value={nameDraft}
                onChange={(e) => setNameDraft(e.target.value)}
                maxLength={64}
              />
              <button type="submit" className="il-btn-buy" disabled={!nameDraft.trim()}>
                Continue
              </button>
            </form>
          </div>
        </div>
      )}
    </>
  );
}
